'use strict';

/**
 * MarkupChat — right-panel AI chat thread scoped to either the selected
 * markup OR the whole session. POSTs to /api/markup-sessions/:id/chats.
 *
 * Threads are persisted, one per (session, markupIdx) tuple. Switching
 * markups loads the matching thread from the server.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as api from './api';

export default function MarkupChat({ sessionId, markupIdx, markup }) {
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const isMarkup = markupIdx !== undefined && markupIdx !== null && markupIdx >= 0;

  // Reload the thread when the scope changes
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    api
      .listChats(sessionId, isMarkup ? markupIdx : null)
      .then((rows) => { if (!cancelled) setTurns(rows); })
      .catch((err) => { if (!cancelled) console.warn('[chat] load failed:', err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, markupIdx, isMarkup]);

  // Auto-scroll to bottom on new turn
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns]);

  const send = useCallback(async () => {
    const msg = draft.trim();
    if (!msg || sending) return;
    setSending(true);
    setDraft('');
    // Optimistic add
    const optimistic = [
      ...turns,
      { id: `pending-u-${Date.now()}`, role: 'user', content: msg },
    ];
    setTurns(optimistic);
    try {
      const res = await api.postChat(sessionId, { message: msg, markupIdx: isMarkup ? markupIdx : null });
      setTurns([
        ...optimistic.filter((t) => !t.id.startsWith('pending-')),
        res.userTurn || { id: `u-${Date.now()}`, role: 'user', content: msg },
        res.assistantTurn || { id: `a-${Date.now()}`, role: 'assistant', content: '(no reply)' },
      ]);
    } catch (err) {
      setTurns([
        ...optimistic,
        { id: `err-${Date.now()}`, role: 'assistant', content: `_Failed: ${err?.response?.data?.error || err.message}_` },
      ]);
    } finally {
      setSending(false);
    }
  }, [draft, sending, turns, sessionId, isMarkup, markupIdx]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="text-sm font-semibold text-slate-700">AI Reviewer</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {isMarkup ? (
            <>Scope: <span className="font-mono">markup #{markupIdx + 1}</span>{markup ? ` · ${markup.type}` : ''}</>
          ) : (
            <>Scope: whole drawing</>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading && (
          <div className="text-xs text-slate-400 text-center py-2">Loading thread…</div>
        )}
        {turns.length === 0 && !loading && (
          <div className="text-xs text-slate-400 text-center py-6">
            Ask about this {isMarkup ? 'markup' : 'drawing'} — refine the label, double-check the rationale, or ask for alternatives.
          </div>
        )}
        {turns.map((t) => (
          <ChatBubble key={t.id} role={t.role} content={t.content} />
        ))}
      </div>

      <div className="border-t border-slate-200 p-2 bg-slate-50">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder={isMarkup
            ? 'Refine this markup… (⌘/Ctrl + Enter to send)'
            : 'Ask about the drawing… (⌘/Ctrl + Enter to send)'}
          className="w-full text-sm border border-slate-300 rounded px-2 py-1 resize-y bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <div className="flex justify-end mt-1">
          <button
            type="button"
            disabled={sending || !draft.trim()}
            onClick={send}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {sending ? 'Thinking…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed
          ${isUser ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}
      >
        {content}
      </div>
    </div>
  );
}
